import React, { useState } from 'react'
import { useSubscriptions, useExpiringSoon } from '@/hooks/admin/useSubscriptions'
import { SubscriptionStatus, SubscriptionPlan } from '@/types/admin'
import type { SubscriptionListDTO } from '@/types/admin'
import type { SubscriptionsPageResponse } from '@/lib/api/admin/subscriptions'
import {
  CreditCard,
  Search,
  Filter,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
} from 'lucide-react'
import { formatCurrency } from '@/utils/currency'

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Attiva',
  EXPIRED: 'Scaduta',
  CANCELLED: 'Cancellata',
  PENDING: 'In attesa',
}

const STATUS_COLORS: Record<SubscriptionStatus, string> = {
  ACTIVE: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  EXPIRED: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  CANCELLED: 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300',
  PENDING: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
}

const PLAN_COLORS: Record<SubscriptionPlan, string> = {
  FREE: 'text-gray-600 dark:text-gray-400',
  STARTER: 'text-blue-600 dark:text-blue-400',
  PRO: 'text-purple-600 dark:text-purple-400',
  ENTERPRISE: 'text-yellow-600 dark:text-yellow-400',
}

const PLAN_PRICES: Record<SubscriptionPlan, number> = {
  FREE: 0,
  STARTER: 29,
  PRO: 79,
  ENTERPRISE: 199,
}

export default function AdminSubscriptionsPage() {
  const [page, setPage] = useState(0)
  const [size] = useState(20)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | ''>('')
  const [planFilter, setPlanFilter] = useState<SubscriptionPlan | ''>('')

  const { data, isLoading, error } = useSubscriptions({
    page,
    size,
    status: statusFilter || undefined,
    plan: planFilter || undefined,
    restaurantName: search || undefined,
  }) as { data?: SubscriptionsPageResponse; isLoading: boolean; error: Error | null }

  const { data: expiringSoon } = useExpiringSoon()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(0)
  }

  const getDaysUntilExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return null
    const expiry = new Date(expiresAt)
    const now = new Date()
    const days = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return days
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Gestione Sottoscrizioni
        </h1>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-600 dark:text-red-400">
            Errore nel caricamento sottoscrizioni: {error.message}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestione Sottoscrizioni
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {data ? `${data.totalElements} sottoscrizioni totali` : 'Caricamento...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CreditCard className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
      </div>

      {/* Expiring Soon Alert */}
      {expiringSoon && expiringSoon.length > 0 && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <h3 className="font-semibold text-orange-900 dark:text-orange-200">
              Sottoscrizioni in Scadenza ({expiringSoon.length})
            </h3>
          </div>
          <div className="space-y-2">
            {expiringSoon.slice(0, 3).map((sub) => {
              const daysLeft = getDaysUntilExpiry(sub.expiresAt)
              return (
                <div
                  key={sub.id}
                  className="flex items-center justify-between bg-white dark:bg-gray-800 rounded p-3 text-sm"
                >
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {sub.restaurantName}
                    </span>
                    <span className={`ml-2 font-semibold ${PLAN_COLORS[sub.plan]}`}>
                      {sub.plan}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">
                      {daysLeft !== null
                        ? daysLeft > 0
                          ? `${daysLeft} giorni rimasti`
                          : 'Scaduta'
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              )
            })}
            {expiringSoon.length > 3 && (
              <p className="text-sm text-orange-700 dark:text-orange-300 text-center">
                +{expiringSoon.length - 3} altre sottoscrizioni in scadenza
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Cerca per nome ristorante..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as SubscriptionStatus | '')
                setPage(0)
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none"
            >
              <option value="">Tutti gli stati</option>
              <option value="ACTIVE">Attive</option>
              <option value="EXPIRED">Scadute</option>
              <option value="CANCELLED">Cancellate</option>
              <option value="PENDING">In attesa</option>
            </select>
          </div>

          {/* Plan Filter */}
          <div className="relative">
            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value as SubscriptionPlan | '')
                setPage(0)
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none"
            >
              <option value="">Tutti i piani</option>
              <option value="FREE">FREE</option>
              <option value="STARTER">STARTER</option>
              <option value="PRO">PRO</option>
              <option value="ENTERPRISE">ENTERPRISE</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-4">Caricamento sottoscrizioni...</p>
          </div>
        ) : !data || data.content.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {search || statusFilter || planFilter
                ? 'Nessuna sottoscrizione trovata con i filtri selezionati'
                : 'Nessuna sottoscrizione presente'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ristorante
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Piano
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Prezzo Mensile
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Data Inizio
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Scadenza
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {data.content.map((subscription) => {
                    const daysLeft = getDaysUntilExpiry(subscription.expiresAt)
                    const isExpiringSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 30

                    return (
                      <tr
                        key={subscription.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {subscription.restaurantName}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-semibold ${PLAN_COLORS[subscription.plan]}`}>
                            {subscription.plan}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              STATUS_COLORS[subscription.status]
                            }`}
                          >
                            {STATUS_LABELS[subscription.status]}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">
                          {formatCurrency(PLAN_PRICES[subscription.plan])}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">
                          <div className="flex items-center justify-end gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(subscription.startDate).toLocaleDateString('it-IT')}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {subscription.expiresAt ? (
                            <div>
                              <div
                                className={`flex items-center justify-end gap-1 ${
                                  isExpiringSoon
                                    ? 'text-orange-600 dark:text-orange-400 font-semibold'
                                    : 'text-gray-600 dark:text-gray-400'
                                }`}
                              >
                                <Calendar className="w-4 h-4" />
                                {new Date(subscription.expiresAt).toLocaleDateString('it-IT')}
                              </div>
                              {isExpiringSoon && daysLeft !== null && (
                                <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                  {daysLeft} giorni rimasti
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Pagina {data.number + 1} di {data.totalPages} • {data.totalElements} risultati
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= data.totalPages - 1}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
